import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { TurnSlots } from "../thread/slots";

/** `ask`, in the terminal register — the question, the command it concerns,
 * and numbered options.
 *
 * The one interactive slot in the vocabulary, and the only place this skin
 * accepts input. The native skin draws buttons in a card; a terminal numbers
 * its choices and waits, so the options render as `[1] Allow once` and the
 * digit is the affordance. That is not decoration: a numbered list is
 * answerable from a keyboard without a pointer, which is the property the
 * register exists to preserve.
 *
 * Once `state` is "decided" the whole thing collapses to one line carrying
 * the question and, when the caller kept it, which option was taken. A
 * settled question is history, and history in a transcript is one row. */
@component({ name: "std-term-turn-ask" })
export class TermTurnAsk extends LonaWebComponent {
  private $title: HTMLElement = this.$("title");
  private $command: HTMLElement = this.$("command");
  private $options: HTMLElement = this.$("options");
  private $decided: HTMLElement = this.$("decided");

  static makeWith(props: TurnSlots.Props["ask"]): TermTurnAsk {
    return (TermTurnAsk.make() as TermTurnAsk).bind(props);
  }

  bind(props: TurnSlots.Props["ask"]): this {
    this.$title.textContent = props.title;

    const command = props.command ?? "";
    this.$command.textContent = command;
    this.$command.style.display = command === "" ? "none" : "";

    const decided = props.state === "decided";
    this.$options.style.display = decided ? "none" : "";
    this.$decided.style.display = decided ? "" : "none";

    if (decided) {
      const taken = props.options.find((o) => o.optionId === props.decidedOptionId);
      // A caller that did not keep the answer still gets a settled line with
      // a neutral mark, rather than a question that looks unanswered.
      this.$decided.textContent = taken === undefined ? "· answered" : `· ${taken.name}`;
      this.$options.replaceChildren();
      return this;
    }

    this.$options.replaceChildren();
    props.options.forEach((option, index) => {
      // A real button, not a styled span: the one interactive slot in the
      // vocabulary must be reachable by keyboard (Tab, then Enter/Space)
      // and must not fire on a drag that happens to end over it — which is
      // also why activation goes through the gesture manager rather than a
      // raw click listener.
      const $option = document.createElement("button");
      $option.type = "button";
      $option.className = "option";
      $option.textContent = `[${index + 1}] ${option.name}`;
      // Prefix match on the protocol's own kind string — the contract keeps
      // `kind` open text, so this styles what it recognises and leaves
      // anything else neutral rather than switching on a closed list.
      if (option.kind.startsWith("allow")) $option.classList.add("allow");
      if (option.kind.startsWith("reject") || option.kind.startsWith("deny")) {
        $option.classList.add("reject");
      }
      GESTURE_MANAGER.addPointerEvent($option, {
        onClick: () => props.onOption?.(option.optionId),
      });
      this.$options.append($option);
    });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
        color: var(--text-pri, #1f2328);
        border-left: 2px solid var(--fg-yellow, #d4a72c);
        padding-left: 1ch;
      }
      #command {
        margin: 2px 0;
        padding: 4px 8px;
        background: var(--surface-sunken, color-mix(in srgb, var(--text-pri, #1f2328) 4%, transparent));
        border-radius: 3px;
        white-space: pre;
        overflow-x: auto;
        color: var(--text-sec, #5c6370);
      }
      #options {
        display: flex;
        flex-wrap: wrap;
        gap: 2ch;
      }
      .option {
        /* Reset the button chrome so the terminal grammar survives: the
           affordance here is the numbered text, not a widget. */
        appearance: none;
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        font: inherit;
        text-align: left;
        cursor: pointer;
        color: var(--text-sec, #5c6370);
      }
      .option:focus-visible {
        outline: 1px solid var(--fg-accent, #4c8bf5);
        outline-offset: 1px;
      }
      .option:hover {
        color: var(--text-pri, #1f2328);
      }
      .option.allow {
        color: var(--fg-green, #2da44e);
      }
      .option.reject {
        color: var(--fg-red, #e5484d);
      }
      #decided {
        color: var(--text-ter, #8b93a7);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="title"></div>
    <pre id="command"></pre>
    <div id="options"></div>
    <div id="decided"></div>
  `;
}
